import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import {
  Alert,
  Box,
  Button,
  Chip,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Paper,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import EditIcon from '@mui/icons-material/EditOutlined'
import DeleteIcon from '@mui/icons-material/DeleteOutline'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import FolderIcon from '@mui/icons-material/FolderOutlined'
import type { Category, CategoryKind, Poste } from '@iobiz/shared'
import { categoryInputSchema, uuidv7 } from '@iobiz/shared'
import { ApiError } from '../api/client.js'
import {
  createCategory,
  deleteCategory,
  flattenCategories,
  listCategories,
  updateCategory,
} from '../features/catalog/api.js'
import { useSessionStore } from '../stores/sessionStore.js'
import { EditeurPostes } from '../features/catalog/EditeurPostes.js'
import { ActionsDOnglet } from '../ui/ActionsDOnglet.js'

/** Arborescence des catégories (W-13). */
export function CategoryTreePage(): JSX.Element {
  const { slug } = useParams<{ slug: string }>()
  const store = useSessionStore((s) => (slug ? s.storeBySlug(slug) : undefined))

  const [tree, setTree] = useState<Category[]>([])
  const [expanded, setExpanded] = useState<Set<string>>(new Set())
  const [editing, setEditing] = useState<Category | null>(null)
  const [creatingUnder, setCreatingUnder] = useState<string | null | undefined>(undefined)
  const [name, setName] = useState('')
  const [parentId, setParentId] = useState('')
  const [banner, setBanner] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  /**
   * ⭐ Deux familles de catégories, séparées par un onglet. Les mêler dans un
   * seul arbre obligerait à lire le type de chaque ligne pour savoir ce qu'on
   * regarde — et la saisie d'un article, elle, ne propose de toute façon que
   * celles de sa famille.
   */
  const [famille, setFamille] = useState<CategoryKind>('product')
  const [kind, setKind] = useState<CategoryKind>('product')
  const [postes, setPostes] = useState<Poste[]>([])

  const reload = (): void => {
    if (!store) return
    void listCategories(store.storeId).then((r) => {
      setTree(r.data)
      // Développe les racines au premier chargement : un arbre entièrement
      // replié n'apprend rien à l'utilisateur.
      setExpanded((prev) => (prev.size === 0 ? new Set(r.data.map((c) => c.id)) : prev))
    })
  }

  useEffect(reload, [store])

  const dialogOpen = editing !== null || creatingUnder !== undefined

  function openCreate(under: string | null): void {
    setEditing(null)
    setCreatingUnder(under)
    setName('')
    setParentId(under ?? '')
    // ⚠️ Une sous-catégorie HÉRITE du type de son parent. Laisser le choix
    // permettrait de ranger un service sous « Produits », où la saisie d'un
    // article ne le proposerait jamais — introuvable sans explication.
    const parent = under ? flattenCategories(tree).find((o) => o.id === under) : undefined
    setKind(parent?.kind ?? famille)
    setPostes([])
    setError(null)
  }

  function openEdit(category: Category): void {
    setEditing(category)
    setCreatingUnder(undefined)
    setName(category.name)
    setParentId(category.parentId ?? '')
    setKind(category.kind)
    setPostes(category.postes)
    setError(null)
  }

  function close(): void {
    setEditing(null)
    setCreatingUnder(undefined)
    setError(null)
  }

  async function save(): Promise<void> {
    if (!store) return
    const parsed = categoryInputSchema.safeParse({
      name,
      parentId: parentId || undefined,
      kind,
      // Une catégorie de produits ne porte pas de postes : le schéma le refuse,
      // et l'oublier ici ferait échouer l'enregistrement après un changement
      // de type sans que le message désigne la cause.
      postes: kind === 'service' ? postes : [],
    })
    if (!parsed.success) {
      setError(parsed.error.issues[0]?.message ?? 'Saisie invalide')
      return
    }
    try {
      if (editing) await updateCategory(store.storeId, editing.id, parsed.data)
      else await createCategory(store.storeId, parsed.data)
      close()
      reload()
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Enregistrement impossible')
    }
  }

  async function remove(category: Category): Promise<void> {
    if (!store) return
    setBanner(null)
    try {
      await deleteCategory(store.storeId, category.id)
      reload()
    } catch (err) {
      // Le serveur refuse si la catégorie contient des articles ou des
      // sous-catégories : son message dit quoi faire, on le relaie tel quel.
      setBanner(err instanceof ApiError ? err.message : 'Suppression impossible')
    }
  }

  function toggle(id: string): void {
    setExpanded((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  function renderNode(category: Category, depth: number): JSX.Element {
    const hasChildren = category.children.length > 0
    const isOpen = expanded.has(category.id)

    return (
      <Box key={category.id}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            py: 2,
            pl: 3 + depth * 6,
            pr: 3,
            borderBottom: 1,
            borderColor: 'divider',
            '&:hover .actions': { opacity: 1 },
          }}
        >
          {hasChildren ? (
            <IconButton size="small" onClick={() => toggle(category.id)} aria-label="Déplier">
              {isOpen ? <ExpandMoreIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}
            </IconButton>
          ) : (
            <Box sx={{ width: 34 }} />
          )}

          <FolderIcon sx={{ fontSize: 18, color: category.color ?? 'text.secondary' }} />
          <Typography variant="body2" sx={{ fontWeight: depth === 0 ? 500 : 400 }}>
            {category.name}
          </Typography>
          <Chip
            size="small"
            variant="outlined"
            label={`${category.itemCount} article${category.itemCount > 1 ? 's' : ''}`}
          />
          {/* ⚠️ Affiché même à zéro sur une catégorie de services : « aucun
              poste » explique un agenda sans créneaux, alors que l'absence de
              pastille laisserait chercher ailleurs. */}
          {category.kind === 'service' && (
            <Chip
              size="small"
              color={category.postes.length === 0 ? 'warning' : 'default'}
              variant="outlined"
              label={
                category.postes.length === 0
                  ? 'aucun poste'
                  : `${category.postes.length} poste${category.postes.length > 1 ? 's' : ''}`
              }
            />
          )}

          <Box sx={{ flex: 1 }} />
          <Box className="actions" sx={{ opacity: 0, transition: 'opacity .15s', display: 'flex' }}>
            <IconButton size="small" onClick={() => openCreate(category.id)} aria-label="Ajouter une sous-catégorie">
              <AddIcon fontSize="small" />
            </IconButton>
            <IconButton size="small" onClick={() => openEdit(category)} aria-label="Modifier">
              <EditIcon fontSize="small" />
            </IconButton>
            <IconButton size="small" onClick={() => void remove(category)} aria-label="Supprimer">
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Box>
        </Box>

        {hasChildren && (
          <Collapse in={isOpen}>{category.children.map((c) => renderNode(c, depth + 1))}</Collapse>
        )}
      </Box>
    )
  }

  // Une catégorie ne peut pas devenir sa propre descendante : on retire du
  // choix de parent la branche en cours d'édition.
  // …et de la MÊME famille : un service rangé sous « Produits » ne serait
  // jamais proposé à la saisie d'un article, donc introuvable.
  const parentOptions = flattenCategories(tree)
    .filter((o) => o.kind === kind)
    .filter((o) => !editing || (o.id !== editing.id && !editing.children.some((c) => c.id === o.id)))

  const racines = tree.filter((c) => c.kind === famille)

  /**
   * Pourquoi le type ne peut pas changer — ou `null` s'il le peut.
   *
   * Le message NOMME les articles qui s'y opposent : « figé » sans raison
   * laisse chercher, et la manœuvre à faire (les reclasser) n'est pas devinable.
   */
  const bloque = ((): string | null => {
    if (!editing) return null
    const intrus = kind === 'service' ? editing.productCount : editing.serviceCount
    if (intrus === 0) return null
    return kind === 'service'
      ? `Impossible : ${intrus} produit(s) sont classés ici. Reclassez-les d'abord.`
      : `Impossible : ${intrus} prestation(s) sont classées ici. Reclassez-les d'abord.`
  })()

  return (
    <>
      {/* ⭐ L'action rejoint la barre d'onglets. La règle « filtrer sur une
          catégorie inclut ses sous-catégories » a suivi le filtre lui-même, au
          catalogue : elle n'éclairait rien en légende d'un titre, à un onglet
          de l'endroit où elle s'applique. */}
      <ActionsDOnglet>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => openCreate(null)}>
          Nouvelle catégorie
        </Button>
      </ActionsDOnglet>

      {/* ⭐ Deux familles, deux arbres. Les mêler obligerait à lire le type de
          chaque ligne pour savoir ce qu'on regarde. */}
      <Tabs value={famille} onChange={(_, v: CategoryKind) => setFamille(v)} sx={{ mb: 4 }}>
        <Tab value="product" label="Produits" />
        <Tab value="service" label="Services" />
      </Tabs>

      {banner && (
        <Alert severity="warning" sx={{ mb: 4 }} onClose={() => setBanner(null)}>
          {banner}
        </Alert>
      )}

      <Paper sx={{ borderRadius: 3, overflow: 'hidden' }}>
        {racines.length === 0 ? (
          <Box sx={{ p: 12, textAlign: 'center' }}>
            <FolderIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 3 }} />
            <Typography variant="h3" sx={{ mb: 2 }}>
              Aucune catégorie
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 5 }}>
              Organisez votre catalogue en créant une première catégorie.
            </Typography>
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => openCreate(null)}>
              Nouvelle catégorie
            </Button>
          </Box>
        ) : (
          racines.map((c) => renderNode(c, 0))
        )}
      </Paper>

      <Dialog open={dialogOpen} onClose={close} fullWidth maxWidth="xs">
        <DialogTitle>{editing ? 'Modifier la catégorie' : 'Nouvelle catégorie'}</DialogTitle>
        <DialogContent dividers>
          {error && (
            <Alert severity="error" sx={{ mb: 4 }}>
              {error}
            </Alert>
          )}
          <TextField
            label="Nom"
            value={name}
            onChange={(e) => setName(e.target.value)}
            fullWidth
            required
            autoFocus
            sx={{ mb: 4 }}
          />
          <TextField
            select
            label="Catégorie parente"
            value={parentId}
            onChange={(e) => setParentId(e.target.value)}
            fullWidth
            sx={{ mb: 4 }}
          >
            <MenuItem value="">Aucune (racine)</MenuItem>
            {parentOptions.map((o) => (
              <MenuItem key={o.id} value={o.id} sx={{ pl: 2 + o.depth * 3 }}>
                {o.label.trim()}
              </MenuItem>
            ))}
          </TextField>

{/* ⚠️ Verrouillé UNIQUEMENT si la catégorie contient des articles de
              l'AUTRE famille : les basculer les rendrait introuvables à la
              saisie, et les prestations disparaîtraient de l'agenda sans que
              rien ne soit supprimé ni dit.
              Une catégorie qui ne contient que des services peut donc devenir
              « Services » sans qu'on exige de la vider — exiger l'inverse
              enfermait dehors qui voulait simplement corriger un classement. */}
          <TextField
            select
            label="Type"
            value={kind}
            disabled={bloque !== null}
            helperText={
              bloque ??
              'Produits ou services — la saisie d’un article ne proposera que sa famille.'
            }
            onChange={(e) => {
              const suivant = e.target.value as CategoryKind
              setKind(suivant)
              if (suivant === 'service' && postes.length === 0) {
                setPostes([{ id: uuidv7(), label: '1', position: 0 }])
              }
            }}
            fullWidth
            sx={{ mb: 4 }}
          >
            <MenuItem value="product">Produits</MenuItem>
            <MenuItem value="service">Services</MenuItem>
          </TextField>

          {kind === 'service' && <EditeurPostes postes={postes} onChange={setPostes} />}
        </DialogContent>
        <DialogActions sx={{ px: 6, py: 4 }}>
          <Button onClick={close}>Annuler</Button>
          <Button variant="contained" onClick={() => void save()}>
            {editing ? 'Enregistrer' : 'Créer'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}
